import { supabase } from '../../config/supabase';
import { User } from '../../types/cms.types';
import { CMSListResponse, CMSApiResponse } from '../../types/cms.types';

export class UserService {
    private static instance: UserService;

    public static getInstance(): UserService {
        if (!UserService.instance) {
            UserService.instance = new UserService();
        }
        return UserService.instance;
    }

    // Get users with pagination and filters
    async getUsers(
        page: number = 1,
        limit: number = 10,
        filters?: {
            location_city?: string;
            search?: string;
            is_active?: boolean;
        }
    ): Promise<CMSListResponse<User>> {
        try {
            let query = supabase
                .from('users')
                .select('*', { count: 'exact' });

            // Apply filters
            if (filters?.location_city && filters.location_city !== '') {
                query = query.eq('location_city', filters.location_city);
            }

            if (filters?.is_active !== undefined) {
                query = query.eq('is_active', filters.is_active);
            }

            // Search filter
            if (filters?.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                query = query.or(`full_name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
            }

            // Apply pagination
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            query = query
                .order('created_at', { ascending: false })
                .range(from, to);

            const { data, count, error } = await query;

            if (error) {
                console.error('Error fetching users:', error);
                return {
                    data: [],
                    pagination: { page, limit, total: 0, totalPages: 0 },
                    error: error.message,
                    success: false
                };
            }

            const total = count || 0;
            const totalPages = Math.ceil(total / limit);

            return {
                data: data || [],
                pagination: { page, limit, total, totalPages },
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in getUsers:', error);
            return {
                data: [],
                pagination: { page, limit, total: 0, totalPages: 0 },
                error: error.message || 'Có lỗi xảy ra khi tải danh sách người dùng',
                success: false
            };
        }
    }

    // Get user by ID
    async getUserById(id: string): Promise<CMSApiResponse<User>> {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                console.error('Error fetching user:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in getUserById:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi tải thông tin người dùng',
                success: false
            };
        }
    }

    // Create new user
    async createUser(userData: Partial<User>): Promise<CMSApiResponse<User>> {
        try {
            // Remove password from user data if empty
            const dataToInsert = { ...userData };
            if (!dataToInsert.pass_word) {
                delete dataToInsert.pass_word;
            }

            const { data, error } = await supabase
                .from('users')
                .insert([dataToInsert])
                .select()
                .single();

            if (error) {
                console.error('Error creating user:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in createUser:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi tạo người dùng',
                success: false
            };
        }
    }

    // Update user
    async updateUser(id: string, userData: Partial<User>): Promise<CMSApiResponse<User>> {
        try {
            // Remove password from user data if empty
            const dataToUpdate = { ...userData };
            if (!dataToUpdate.pass_word) {
                delete dataToUpdate.pass_word;
            }

            // Update timestamp
            dataToUpdate.updated_at = new Date().toISOString();

            const { data, error } = await supabase
                .from('users')
                .update(dataToUpdate)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error updating user:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in updateUser:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi cập nhật người dùng',
                success: false
            };
        }
    }

    // Delete user
    async deleteUser(id: string): Promise<CMSApiResponse<boolean>> {
        try {
            const { error } = await supabase
                .from('users')
                .delete()
                .eq('id', id);

            if (error) {
                console.error('Error deleting user:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data: true,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in deleteUser:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi xóa người dùng',
                success: false
            };
        }
    }

    // Toggle user active status
    async toggleUserStatus(id: string, isActive: boolean): Promise<CMSApiResponse<User>> {
        try {
            const { data, error } = await supabase
                .from('users')
                .update({
                    is_active: isActive,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error toggling user status:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in toggleUserStatus:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi thay đổi trạng thái người dùng',
                success: false
            };
        }
    }

    // Get user statistics
    async getUserStatistics(): Promise<CMSApiResponse<{
        total: number;
        active: number;
        inactive: number;
    }>> {
        try {
            // Get total users
            const { count: totalCount, error: totalError } = await supabase
                .from('users')
                .select('*', { count: 'exact', head: true });

            if (totalError) throw totalError;

            // Get active users
            const { count: activeCount, error: activeError } = await supabase
                .from('users')
                .select('*', { count: 'exact', head: true })
                .eq('is_active', true);

            if (activeError) throw activeError;

            return {
                data: {
                    total: totalCount || 0,
                    active: activeCount || 0,
                    inactive: (totalCount || 0) - (activeCount || 0)
                },
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in getUserStatistics:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi lấy thống kê người dùng',
                success: false
            };
        }
    }
}
